import { Data, Model, Utils } from '@singularsystems/neo-core';
import { AxiosPromise } from 'axios';
import { injectable } from 'inversify';
import { AppService, Types } from '../DomainTypes';
import { IInvestmentHostApiClient } from './IInvestmentHostApiClient';
import IssuerInvestmentPagedLookupCriteria from '../Models/IssuerInvestments/Lookups/IssuerInvestmentPagedLookupCriteria';
import IssuerInvestmentPagedLookup from '../Models/IssuerInvestments/Lookups/IssuerInvestmentPagedLookup';
import IssuerInvestment from '../Models/IssuerInvestments/IssuerInvestment';
import IssuerInvestmentLookup from '../Models/IssuerInvestments/Lookups/IssuerInvestmentLookup';
import UtilisationCriteria from '../Models/IssuerInvestments/Lookups/UtilisationCriteria';
import UtilisationLookup from '../Models/IssuerInvestments/Lookups/UtilisationLookup';
import ShareTransactionCommand from '../Models/Transactions/Commands/ShareTransactionCommand';
import CreateTransactionResult from '../Models/Transactions/Lookups/CreateTransactionResult';
import DeleteTransactionResult from '../Models/Transactions/Lookups/DeleteTransactionResult';
import LiquidityProvider from '../Models/InvestorInvestments/LiquidityProvider';
import { IInvestmentHostSaveResult } from '../Models/Investment/Lookups/InvestmentSaveResult';
import DeleteTransactionCommand from '../Models/Transactions/Commands/DeleteTransactionCommand';
import LinkedInvestorInvestmentLookup from '../Models/InvestorInvestments/Lookups/LinkedInvestorInvestmentLookup';
import RollbackAllocationsCommand from '../Models/IssuerInvestments/Commands/RollbackAllocationsCommand';
import QPTracking from '../Models/IssuerInvestments/QPTracking';

export interface IIssuerInvestmentApiClient extends IInvestmentHostApiClient {

    /**
     * Gets a list of issuer investments for a paged grid.
     */
    getIssuerInvestmentsPaged(pageRequest: Model.PartialPlainNonTrackedObject<Data.PageRequest<IssuerInvestmentPagedLookupCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<IssuerInvestmentPagedLookup>>>;

    /**
     * Gets an issuer investment for a given id.
     */
    getIssuerInvestment(issuerInvestmentId: number): AxiosPromise<Model.PlainTrackedObject<IssuerInvestment>>;

    /**
     * Gets the liquidity providers for a given issuer investment.
     */
    getLiquidityProviders(issuerInvestmentId: number): AxiosPromise<Array<Model.PlainTrackedObject<LiquidityProvider>>>;

    /** 
     * Gets the QPTracking list of investor investments for a given issuer investment.
     */
    getQPTrackingList(issuerInvestmentId: number): AxiosPromise<Array<Model.PlainTrackedObject<QPTracking>>>;

    /**
     * Saves an liquidity provider.
     */
    saveLiquidityProviders(liquidityProviders: Array<Model.PartialPlainObject<LiquidityProvider>>): AxiosPromise<Array<Model.PlainTrackedObject<LiquidityProvider>>>;

    /**
     * Gets a list of issuer investments that match the search text. Limited to 50 results.
     */
    getIssuerInvestmentLookups(searchText: string, entityId: number | null, productTypeId: number | null): AxiosPromise<Array<Model.PlainObject<IssuerInvestmentLookup>>>;

    /**
     * Gets a cached list of issuer investments if the database contains a max of 10 investments.
     */
    getCachedParentIssuerInvestments(): AxiosPromise<Array<Model.PlainObject<IssuerInvestmentLookup>>>;

    /**
     * Gets utilisation info for an issuer investment.
     */
    getUtilisation(criteria: Model.PartialPlainNonTrackedObject<UtilisationCriteria>): AxiosPromise<Model.PlainObject<UtilisationLookup>>;

    /**
     * Saves an issuer investment.
     */
    saveIssuerInvestment(issuerInvestment: Model.PartialPlainObject<IssuerInvestment>): AxiosPromise<IInvestmentHostSaveResult<IssuerInvestment>>;

    /** 
     * Deletes share allocations after the given date, and updates the last allocation date on the issuer investment.
     */
    rollbackShareAllocations(command: Model.PartialPlainObject<RollbackAllocationsCommand>): AxiosPromise;

    /**
     * Gets the list of investor investments that are linked to the given issuer investment id, excluding liquidity providers.
     */
    getLinkedInvestorInvestments(issuerInvestmentId: number): AxiosPromise<Array<Model.PlainObject<LinkedInvestorInvestmentLookup>>>;

    /**
     * Creates a share transaction from the details in the command.
     */
    createShareTransaction(command: Model.PartialPlainObject<ShareTransactionCommand>): AxiosPromise<Model.PlainObject<CreateTransactionResult>>;

    /**
     * Deletes the share transaction with the given share transaction id.
     */
    deleteShareTransaction(command: Model.PartialPlainNonTrackedObject<DeleteTransactionCommand>): AxiosPromise;

    getUploadParticipationAgreementFileUrl(): string;

    deleteParticipationAgreement(participationAgreementId: number): AxiosPromise;

    // Client only properties / methods
}

@injectable()
export default class IssuerInvestmentApiClient extends Data.ApiClientBase implements IIssuerInvestmentApiClient {

    constructor(config = AppService.get(Types.App.Config)) {
        super(`${config.apiPath}/issuer-investments`);
    }

    public getIssuerInvestmentsPaged(pageRequest: Model.PartialPlainNonTrackedObject<Data.PageRequest<IssuerInvestmentPagedLookupCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<IssuerInvestmentPagedLookup>>> {
        return this.axios.get(`${this.apiPath}/paged?${Utils.getQueryString(pageRequest)}`);
    }

    public getIssuerInvestment(issuerInvestmentId: number): AxiosPromise<Model.PlainTrackedObject<IssuerInvestment>> {
        return this.axios.get(`${this.apiPath}/${issuerInvestmentId}`);
    }

    public getLiquidityProviders(issuerInvestmentId: number): AxiosPromise<Array<Model.PlainTrackedObject<LiquidityProvider>>> {
        return this.axios.get(`${this.apiPath}/liquidity-providers?issuerInvestmentId=${issuerInvestmentId}`);
    }

    public getQPTrackingList(issuerInvestmentId: number): AxiosPromise<Array<Model.PlainTrackedObject<QPTracking>>> {
        return this.axios.get(`${this.apiPath}/qptracking?issuerInvestmentId=${issuerInvestmentId}`);
    }

    public saveLiquidityProviders(liquidityProviders: Array<Model.PartialPlainObject<LiquidityProvider>>): AxiosPromise<Array<Model.PlainTrackedObject<LiquidityProvider>>> {
        return this.axios.post(`${this.apiPath}/liquidity-providers/save`, liquidityProviders);
    }

    public getIssuerInvestmentLookups(searchText: string, entityId: number | null, productTypeId: number | null): AxiosPromise<Array<Model.PlainObject<IssuerInvestmentLookup>>> {
        var url = `${this.apiPath}/lookups?searchText=${encodeURIComponent(searchText)}`;
        if (entityId) {
            url += `&entityId=${entityId}`
        }
        if (productTypeId) {
            url += `&productTypeId=${productTypeId}`;
        }
        return this.axios.get(url);
    }

    public getCachedParentIssuerInvestments(): AxiosPromise<Array<Model.PlainObject<IssuerInvestmentLookup>>> {
        return this.axios.get(`${this.apiPath}/cached-parent-lookups`);
    }

    public getUtilisation(criteria: Model.PartialPlainNonTrackedObject<UtilisationCriteria>): AxiosPromise<Model.PlainObject<UtilisationLookup>> {
        return this.axios.get(`${this.apiPath}/utilisation?${Utils.getQueryString(criteria)}`);
    }

    public saveIssuerInvestment(issuerInvestment: Model.PartialPlainObject<IssuerInvestment>): AxiosPromise<IInvestmentHostSaveResult<IssuerInvestment>> {
        return this.axios.post(`${this.apiPath}`, issuerInvestment);
    }

    public rollbackShareAllocations(command: Model.PartialPlainObject<RollbackAllocationsCommand>): AxiosPromise {
        return this.axios.post(`${this.apiPath}/rollback-share-allocations`, command);
    }

    public getLinkedInvestorInvestments(issuerInvestmentId: number): AxiosPromise<Array<Model.PlainObject<LinkedInvestorInvestmentLookup>>> {
        return this.axios.get(`${this.apiPath}/linkedInvestorInvestments?issuerInvestmentId=${issuerInvestmentId}`);
    }

    public createShareTransaction(command: Model.PartialPlainObject<ShareTransactionCommand>): AxiosPromise<Model.PlainObject<CreateTransactionResult>> {
        return this.axios.post(`${this.apiPath}/transaction`, command);
    }

    public deleteShareTransaction(command: Model.PartialPlainNonTrackedObject<DeleteTransactionCommand>): AxiosPromise<Model.PlainObject<DeleteTransactionResult>> {
        return this.axios.delete(`${this.apiPath}/transaction/${command.shareTransactionId}`);
    }

    public getUploadParticipationAgreementFileUrl(): string {
        return `${this.apiPath}/upload-participation-agreement`;
    }

    public deleteParticipationAgreement(participationAgreementId: number): AxiosPromise {
        return this.axios.delete(`${this.apiPath}/participation-agreement/${participationAgreementId}`);
    }

    // Client only properties / methods
}